import { defineMessages } from 'react-intl';
import { invert, uniq } from 'lodash';

import {
  FACET,
  MultiselectDropdownItem,
  OPERATOR,
  SEARCH_OPTION,
} from '@app/components';
import { sharedMessages } from '@app/translations';

export enum ADVANCED_SEARCH_FIELD {
  keywordAnywhere = 'keywordAnywhere',
  titleAll = 'titleAll',
  titleMain = 'titleMain',
  authorCreator = 'authorCreator',
  subjectAll = 'subjectAll',
  nameAll = 'nameAll',
  namePersonal = 'namePersonal',
  identifiers = 'lccnIsbnIssn',
  series = 'series',
}

export const ADVANCED_SEARCH_FIELDS = Object.values(ADVANCED_SEARCH_FIELD);

export const ADVANCED_SEARCH_OPTIONS = {
  keyword: SEARCH_OPTION.keyword,
  title: 'allTitles',
  titleMain: 'titleMain',
  contributors: 'contributors.name',
  nameAll: 'allNames',
  namePersonal: 'personalContributors',
  subjects: 'subjects.value',
  identifiers: 'identifiers.value',
  series: 'series.value',
};

export const FIELDS_TO_SEARCH_OPTION_MAP = {
  [ADVANCED_SEARCH_FIELD.keywordAnywhere]: ADVANCED_SEARCH_OPTIONS.keyword,
  [ADVANCED_SEARCH_FIELD.titleAll]: ADVANCED_SEARCH_OPTIONS.title,
  [ADVANCED_SEARCH_FIELD.titleMain]: ADVANCED_SEARCH_OPTIONS.titleMain,
  [ADVANCED_SEARCH_FIELD.authorCreator]: ADVANCED_SEARCH_OPTIONS.contributors,
  [ADVANCED_SEARCH_FIELD.subjectAll]: ADVANCED_SEARCH_OPTIONS.subjects,
  [ADVANCED_SEARCH_FIELD.nameAll]: ADVANCED_SEARCH_OPTIONS.nameAll,
  [ADVANCED_SEARCH_FIELD.namePersonal]: ADVANCED_SEARCH_OPTIONS.namePersonal,
  [ADVANCED_SEARCH_FIELD.identifiers]: ADVANCED_SEARCH_OPTIONS.identifiers,
  [ADVANCED_SEARCH_FIELD.series]: ADVANCED_SEARCH_OPTIONS.series,
};

export const FIELDS_TO_MASK_MAP = {
  [ADVANCED_SEARCH_FIELD.keywordAnywhere]: 'keyword',
  [ADVANCED_SEARCH_FIELD.titleAll]: 'titleAll',
  [ADVANCED_SEARCH_FIELD.titleMain]: 'titleMain',
  [ADVANCED_SEARCH_FIELD.authorCreator]: 'contributor',
  [ADVANCED_SEARCH_FIELD.subjectAll]: 'subject',
  [ADVANCED_SEARCH_FIELD.nameAll]: 'nameAll',
  [ADVANCED_SEARCH_FIELD.namePersonal]: 'namePersonal',
  [ADVANCED_SEARCH_FIELD.identifiers]: 'identifier',
  [ADVANCED_SEARCH_FIELD.series]: 'series',
};

export const SEARCH_OPTION_TO_FIELD_MAP = invert(FIELDS_TO_SEARCH_OPTION_MAP);

export enum ADVANCED_SEARCH_QUALIFIER {
  containsAll = 'containsAll',
  containsAny = 'containsAny',
  exactPhrase = 'exactPhrase',
  startsWith = 'startsWith',
}

export const ADVANCED_SEARCH_QUALIFIERS = Object.keys(
  ADVANCED_SEARCH_QUALIFIER
);

export const SEARCH_OPERATOR_MAP = {
  [ADVANCED_SEARCH_QUALIFIER.containsAll]: OPERATOR.ALL,
  [ADVANCED_SEARCH_QUALIFIER.containsAny]: OPERATOR.ANY,
  [ADVANCED_SEARCH_QUALIFIER.exactPhrase]: OPERATOR.PHRASE_MATCH,
  [ADVANCED_SEARCH_QUALIFIER.startsWith]: OPERATOR.ALL,
};

export const SEARCH_QUALIFIER_MASK_MAP = {
  [ADVANCED_SEARCH_QUALIFIER.containsAll]: 'containsAll',
  [ADVANCED_SEARCH_QUALIFIER.containsAny]: 'containsAny',
  [ADVANCED_SEARCH_QUALIFIER.exactPhrase]: 'exactPhrase',
  [ADVANCED_SEARCH_QUALIFIER.startsWith]: 'startsWith',
};

export enum BOOLEAN_CHIP {
  and = 'and',
  or = 'or',
  not = 'not',
}

export const advancedSearchMessages = defineMessages({
  [BOOLEAN_CHIP.and]: {
    id: 'advancedSearch.and',
    defaultMessage: 'And',
  },
  [BOOLEAN_CHIP.or]: {
    id: 'advancedSearch.or',
    defaultMessage: 'Or',
  },
  [BOOLEAN_CHIP.not]: {
    id: 'advancedSearch.not',
    defaultMessage: 'Not',
  },
  [ADVANCED_SEARCH_QUALIFIER.containsAll]: {
    id: 'advancedSearch.containsAll',
    defaultMessage: 'Contains all',
  },
  [ADVANCED_SEARCH_QUALIFIER.containsAny]: {
    id: 'advancedSearch.containsAny',
    defaultMessage: 'Contains any',
  },
  [ADVANCED_SEARCH_QUALIFIER.exactPhrase]: {
    id: 'advancedSearch.exactPhrase',
    defaultMessage: 'Exact phrase',
  },
  [ADVANCED_SEARCH_QUALIFIER.startsWith]: {
    id: 'advancedSearch.startsWith',
    defaultMessage: 'Starts with',
  },
  [ADVANCED_SEARCH_FIELD.titleAll]: {
    id: 'advancedSearch.titleAll',
    defaultMessage: 'Title: all',
  },
  [ADVANCED_SEARCH_FIELD.titleMain]: {
    id: 'advancedSearch.titleMain',
    defaultMessage: 'Title: main',
  },
  [ADVANCED_SEARCH_FIELD.authorCreator]: {
    id: 'advancedSearch.authorCreator',
    defaultMessage: 'Author/creator',
  },
  [ADVANCED_SEARCH_FIELD.subjectAll]: {
    id: 'advancedSearch.subjectAll',
    defaultMessage: 'Subject: all',
  },
  [ADVANCED_SEARCH_FIELD.nameAll]: {
    id: 'advancedSearch.nameAll',
    defaultMessage: 'Name: all',
  },
  [ADVANCED_SEARCH_FIELD.namePersonal]: {
    id: 'advancedSearch.namePersonal',
    defaultMessage: 'Name: personal',
  },
  [ADVANCED_SEARCH_FIELD.identifiers]: {
    id: 'advancedSearch.identifiers',
    defaultMessage: 'Identifiers (ISBN, ISSN, LCCN, etc)',
  },
  [ADVANCED_SEARCH_FIELD.keywordAnywhere]: sharedMessages.keyword,
  [ADVANCED_SEARCH_FIELD.series]: sharedMessages.series,
});

export const defaultAdvancedSearchRowState = {
  value: '',
  field: ADVANCED_SEARCH_FIELD.keywordAnywhere,
  qualifier: ADVANCED_SEARCH_QUALIFIER.containsAll,
};

export const MAX_ROW_COUNT = 7;

export const TYPES_MAP = {
  chip: [BOOLEAN_CHIP.and, BOOLEAN_CHIP.or, BOOLEAN_CHIP.not],
  field: ADVANCED_SEARCH_FIELDS,
  qualifier: ADVANCED_SEARCH_QUALIFIERS,
};

export const ADVANCED_SEARCH_FORM_ROW_COUNT = 3;

export const BOOLEAN_OPERATOR_REGEX = '( AND | OR | NOT )';

export const FIELDS_REGEX = [...uniq(Object.values(FIELDS_TO_MASK_MAP))].join(
  '|'
);

export const QUALIFIERS_REGEX = Object.values(SEARCH_QUALIFIER_MASK_MAP).join(
  '|'
);

export type SearchLimiterKeys =
  | typeof FACET.FORMAT
  | typeof FACET.LANGUAGE
  | typeof FACET.LOCATION;

export type SearchLimiters = Record<
  SearchLimiterKeys,
  MultiselectDropdownItem[] | undefined
>;

export const limitersDefaultValue = {
  [FACET.FORMAT]: [] as MultiselectDropdownItem[],
  [FACET.LANGUAGE]: [] as MultiselectDropdownItem[],
  [FACET.LOCATION]: [] as MultiselectDropdownItem[],
};

export type UnmappedParsedSearchLimiters = Record<
  SearchLimiterKeys,
  string[] | null
>;

export const defaultUnmappedLimitersValue = {
  [FACET.FORMAT]: [],
  [FACET.LANGUAGE]: [],
  [FACET.LOCATION]: [],
};

export interface RowValues {
  value: string;
  field: ADVANCED_SEARCH_FIELD;
  qualifier: ADVANCED_SEARCH_QUALIFIER;
  booleanChip?: BOOLEAN_CHIP;
  isInvalid?: boolean;
}

export type FormValues = Record<number, RowValues>;
